import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { IClinicContext } from '../models/clinic-context.model';
import { GlobalConfigService } from './global-config.service';

/** Chave utilizada para persistir a clínica ativa no localStorage */
const STORAGE_KEY = 'activeClinic';

/**
 * ContextService — Fonte de Verdade para a Clínica Ativa.
 *
 * Responsabilidades:
 *  - Manter o estado reativo da clínica ativa (BehaviorSubject).
 *  - Persistir / recuperar a escolha do usuário via localStorage (btoa/atob).
 *  - Fornecer métodos para ler, trocar e limpar o contexto.
 *  - Fazer fallback para a API quando o localStorage estiver vazio/inválido.
 */
@Injectable({
  providedIn: 'root'
})
export class ContextService {

  private activeClinicSubject = new BehaviorSubject<IClinicContext | null>(null);

  /** Observable público — componentes devem assinar este stream para reagir a mudanças. */
  public activeClinic$: Observable<IClinicContext | null> = this.activeClinicSubject.asObservable();

  /** Lista completa de clínicas disponíveis para o usuário (populada após loadClinicsAndSetDefault). */
  private availableClinics: IClinicContext[] = [];

  /** Indica se a lista de clínicas já foi carregada com sucesso ao menos uma vez. */
  private clinicsLoaded = false;

  /** Observable em andamento para evitar chamadas HTTP duplicadas (shareReplay). */
  private loadingClinics$: Observable<IClinicContext | null> | null = null;

  private get clinicsUrl(): string {
    return this.globalConfig.getApiUrl() + '/users/me/clinics';
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) {
    this.restoreFromStorage();
  }

  // ─── Métodos Públicos ───────────────────────────────────────────────

  /**
   * Retorna o Observable da clínica ativa.
   * Componentes que precisam reagir à troca de clínica devem usar este método.
   */
  getActiveClinic(): Observable<IClinicContext | null> {
    return this.activeClinic$;
  }

  /**
   * Retorna o valor síncrono da clínica ativa (snapshot).
   * Útil para o interceptor, que precisa de um valor imediato.
   */
  getActiveClinicSnapshot(): IClinicContext | null {
    return this.activeClinicSubject.getValue();
  }

  /**
   * Define a nova clínica ativa, emite para todos os assinantes e persiste no localStorage.
   */
  setActiveClinic(clinic: IClinicContext): void {
    this.activeClinicSubject.next(clinic);
    this.saveToStorage(clinic);
  }

  /**
   * Limpa o contexto da clínica ativa (utilizado no logout).
   */
  clearContext(): void {
    this.activeClinicSubject.next(null);
    localStorage.removeItem(STORAGE_KEY);
    this.availableClinics = [];
    this.clinicsLoaded = false;
    this.loadingClinics$ = null;
  }

  /**
   * Retorna a lista de clínicas disponíveis para o usuário (carregada pela API).
   */
  getAvailableClinics(): IClinicContext[] {
    return this.availableClinics;
  }

  /**
   * Indica se a lista de clínicas já foi carregada.
   */
  isClinicsLoaded(): boolean {
    return this.clinicsLoaded;
  }

  /**
   * Carrega a lista de clínicas do usuário logado e inicializa o contexto.
   * Chamado após o login ou quando o localStorage estiver vazio/inválido.
   *
   * Fluxo:
   *  1. GET /users/me/clinics
   *  2. Mapeia para IClinicContext[] e armazena internamente.
   *  3. Se já existe uma clínica ativa (via localStorage) e ela está na lista, mantém.
   *  4. Caso contrário, seleciona a isDefault ou a primeira da lista.
   */
  loadClinicsAndSetDefault(forceReload = false): Observable<IClinicContext | null> {
    // Se já carregou e não é forceReload, retorna o resultado cacheado
    if (this.clinicsLoaded && !forceReload) {
      return of(this.activeClinicSubject.getValue());
    }

    // Se já tem uma chamada HTTP em andamento, reutiliza
    if (this.loadingClinics$) {
      return this.loadingClinics$;
    }

    this.loadingClinics$ = this.http.get<any[]>(this.clinicsUrl).pipe(
      map((clinics) => {
        if (!clinics || clinics.length === 0) {
          this.availableClinics = [];
          return null;
        }

        // Mapeia a resposta da API para IClinicContext[] e ordena alfabeticamente
        this.availableClinics = clinics.map((c: any) => ({
          // Prefer explicit id, but fall back to CNPJ if id is missing (some APIs may not return id)
          id: c.id || c.cnpj || '',
          cnpj: c.cnpj || '',
          name: c.corporate_name || c.name || '',
          state: c.state || undefined,
          neighborhood: c.neighborhood || undefined,
          groupId: c.group_id || undefined,
          groupName: c.group_name || undefined,
          modules: (c.modules || []).map((m: any) => ({ id: m.id || '', description: m.description || '' })),
          hasTreatmentPlans: c.has_treatment_plans || false,
          hasCalculator: c.has_calculator || false,
          contractedAnamneseType: c.contracted_anamnese_type || 'NOT_CONTRACTED',
          signDeliveryType: c.sign_delivery_type || 'DELIVERY_METHOD_SMS',
          isModelPatientActive: c.is_model_patient_active || false,
          logo: c.logo || '',
          sharePatients: c.share_patients || false,
          shareProcedures: c.share_procedures || false,
          shareProviders: c.share_providers || false
        })).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

        // Se já há uma clínica ativa (restaurada do localStorage), verificar se ainda é válida
        const current = this.activeClinicSubject.getValue();
        if (current?.cnpj) {
          const stillValid = this.availableClinics.find(c => c.cnpj === current.cnpj);
          if (stillValid) {
            // Clínicas sem grupo podem não retornar todos os campos company-level
            // na API /users/me/clinics. Preserva os valores da clínica ativa (vindos do login)
            // quando a API retornou valores padrão/fallback.
            this.mergeClinicCompanyDefaults(stillValid, current);
            return stillValid; // Mantém a seleção existente
          }
        }

        // ROOT: inicia sem clínica ativa (visão ROOT) — não auto-seleciona
        if (this.isRootUser()) {
          return null;
        }

        // Seleciona a clínica padrão ou a primeira — usa availableClinics (que já contém modules)
        const defaultRaw = clinics.find((c: any) => c.isDefault || c.is_default) || clinics[0];
        const defaultCnpj = defaultRaw.cnpj || '';
        const context = this.availableClinics.find(c => c.cnpj === defaultCnpj)
          || this.availableClinics[0];

        return context || null;
      }),
      tap((context) => {
        if (context) {
          this.setActiveClinic(context);
        }
        this.clinicsLoaded = true;
        this.loadingClinics$ = null;
      }),
      catchError((err) => {
        console.error('[ContextService] Falha ao carregar clínicas do usuário:', err);
        this.loadingClinics$ = null;
        return of(null);
      })
    );

    return this.loadingClinics$;
  }

  // ─── Métodos Privados ───────────────────────────────────────────────

  /**
   * Tenta restaurar a clínica ativa do localStorage na inicialização.
   * Se o dado estiver inválido/corrompido, remove e deixa null (o fluxo de
   * inicialização pós-login chamará loadClinicsAndSetDefault).
   */
  private restoreFromStorage(): void {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return; // Nenhuma clínica salva — aguarda o login chamar loadClinicsAndSetDefault
    }

    try {
      const decoded = atob(stored);
      const clinic: IClinicContext = JSON.parse(decoded);

      // Validação básica: precisa ter pelo menos cnpj
      if (clinic && clinic.cnpj) {
        this.activeClinicSubject.next(clinic);
      } else {
        console.warn('[ContextService] Dado do localStorage inválido (sem cnpj). Limpando...');
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.warn('[ContextService] Erro ao decodificar activeClinic do localStorage. Limpando...', e);
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Persiste a clínica ativa no localStorage usando btoa (padrão do projeto).
   */
  private saveToStorage(clinic: IClinicContext): void {
    localStorage.setItem(STORAGE_KEY, btoa(JSON.stringify(clinic)));
  }

  /** Verifica se o usuário logado é ROOT (leitura direta do localStorage para evitar dependência circular com AuthService) */
  private isRootUser(): boolean {
    try {
      return atob(localStorage.getItem('permission') || '') === 'ROOT';
    } catch {
      return false;
    }
  }

  /**
   * Preserva campos company-level na clínica da API quando estes vieram com valores
   * padrão (fallback), mas a clínica de origem (login/localStorage) possui valores reais.
   *
   * Isso corrige o cenário de clínicas sem grupo, em que o endpoint /users/me/clinics
   * pode não devolver campos como contracted_anamnese_type e is_model_patient_active,
   * fazendo o frontend sobrescrever os valores corretos vindos do login.
   */
  private mergeClinicCompanyDefaults(target: IClinicContext, source: IClinicContext): void {
    // contracted_anamnese_type
    if ((!target.contractedAnamneseType || target.contractedAnamneseType === 'NOT_CONTRACTED')
        && source.contractedAnamneseType && source.contractedAnamneseType !== 'NOT_CONTRACTED') {
      target.contractedAnamneseType = source.contractedAnamneseType;
    }

    // is_model_patient_active
    if (!target.isModelPatientActive && source.isModelPatientActive) {
      target.isModelPatientActive = source.isModelPatientActive;
    }

    // has_treatment_plans
    if (!target.hasTreatmentPlans && source.hasTreatmentPlans) {
      target.hasTreatmentPlans = source.hasTreatmentPlans;
    }

    // has_calculator
    if (!target.hasCalculator && source.hasCalculator) {
      target.hasCalculator = source.hasCalculator;
    }

    // sign_delivery_type
    if ((!target.signDeliveryType || target.signDeliveryType === 'DELIVERY_METHOD_SMS')
        && source.signDeliveryType && source.signDeliveryType !== 'DELIVERY_METHOD_SMS') {
      target.signDeliveryType = source.signDeliveryType;
    }
  }
}
